import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageSeo } from '../seo/PageSeo';
import { breadcrumbSchema } from '../seo/schemas';
import { localePrefixedPath } from '../seo/constants';
import { listArticles, hasDistinctLatest, ArticleEntry } from './articles';
import { formatArticleDate } from './formatArticleDate';
import { BlogIndexMessages as M, BlogIndexText } from './BlogIndexPage.messages';
import { Footer } from '../homepage/Footer';
import './blog.css';

/**
 * One article, as a card that is entirely one link.
 *
 * The heading is the link's accessible name and the rest is inside the same anchor,
 * which keeps the target big without leaving screen-reader users a list of
 * indistinguishable "read more" links.
 */
const ArticleCard: React.FC<{
  entry: ArticleEntry;
  href: string;
  locale: string;
  featured?: boolean;
  featuredLabel?: string;
}> = ({ entry, href, locale, featured = false, featuredLabel }) => {
  const fm = entry.module.frontmatter;
  return (
    <Link to={href} className={`blog-card${featured ? ' blog-card--featured' : ''}`}>
      {featured && featuredLabel && <span className="blog-featured-label">{featuredLabel}</span>}
      {fm.tags && fm.tags.length > 0 && (
        <div className="blog-tags">
          {fm.tags.slice(0, 3).map(tag => (
            <span key={tag} className="blog-tag">{tag}</span>
          ))}
        </div>
      )}
      <h2 className="blog-card-title">{fm.title}</h2>
      <p className="blog-card-excerpt">{fm.description}</p>
      <div className="blog-card-meta">
        <time dateTime={fm.date}>{formatArticleDate(fm.date, locale)}</time>
        <span className="blog-card-meta-sep" aria-hidden="true">·</span>
        <span>{fm.author}</span>
      </div>
    </Link>
  );
};

export const BlogIndexPage: React.FC = () => {
  const articles = listArticles();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  // Newest article gets the wide card. listArticles() already sorts by date
  // descending, so the split is positional rather than a second sort.
  const [featured, ...rest] = articles;
  // Only claim "Latest" when it is true. If several articles share the newest date
  // the first card is chosen by declaration order, and badging that would be a
  // confident statement about something arbitrary.
  const showLatestLabel = hasDistinctLatest(articles);

  return (
    <>
      <PageSeo
        path="/blog"
        titleKey="seo.blog.title"
        descriptionKey="seo.blog.description"
        ogImage="/og/og-default.png"
        keywords={['localization blog', 'i18n tutorials', 'ai translation guides']}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ]),
        ]}
      />
      <div className="page-wrapper">
        <div className="blog-page">
          <header className="blog-header">
            <h1>{t(M.PAGE_TITLE, { defaultValue: BlogIndexText.PAGE_TITLE })}</h1>
            <p>{t(M.PAGE_LEDE, { defaultValue: BlogIndexText.PAGE_LEDE })}</p>
          </header>

          {articles.length === 0 ? (
            <p>{t(M.EMPTY, { defaultValue: BlogIndexText.EMPTY })}</p>
          ) : (
            <>
              <ArticleCard
                entry={featured}
                href={localePrefixedPath(`/blog/${featured.slug}`, pathname)}
                locale={i18n.language}
                featured
                featuredLabel={showLatestLabel ? t(M.LATEST, { defaultValue: BlogIndexText.LATEST }) : undefined}
              />
              {rest.length > 0 && (
                <div className="blog-grid">
                  {rest.map(entry => (
                    <ArticleCard
                      key={entry.slug}
                      entry={entry}
                      href={localePrefixedPath(`/blog/${entry.slug}`, pathname)}
                      locale={i18n.language}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
};
